import { authApi, publicApi, siteApi } from "@api";
import { WSBlacklistUpdateEvent, WSBlacklistUpdateEventDetail } from "@constants";
import { Paths } from "@constants/paths";
import { AppDispatch, RootState } from "@stores";
import auth from "@stores/auth";
import { selectAuthStatus } from "@stores/auth/selectAuthStatus";
import authSlice, { AuthSliceState } from "@stores/auth/slices.ts";
import preferenceSlice from "@stores/preference/slices.ts";
import skinnerBoxSlice from "@stores/skinnerBox/slices";
import { AuthStatus, BankAccount, UserMode } from "@types";
import { isGoogleCredential } from "@utils/personal-info";
import { FundingProfileType, GroupCriteriaType } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useWSBlacklistUpdate } from "./useAppWS";

interface UseAuthSessionOptions {
  onBlacklistUpdate?: (event: WSBlacklistUpdateEventDetail) => void;
}

/**
 * Get the current auth session.
 * @return { self, oauth, status, bankAccount, logout, bankInfos, userMode, latestDepositAmount, isBlacklisted }
 * - self: Current user profile
 * - oauth: Current auth token
 * - status: Current auth status
 * - defaultBankAccount: Current user bank account
 * - logout: Logout function
 * - bankInfos: All bank information
 * - userMode: Current user mode, use for UI display
 * - latestDepositAmount: Latest deposit amount
 * - isBlacklisted: Whether the user is blacklisted
 */
const useAuthSession = (options?: UseAuthSessionOptions) => {
  const {
    self,
    oauth,
    bankInfos,
    ewalletInfos,
    latestDepositAmount,
    credential,
    hasGoogleCredential,
    googleAccessKey,
    fundingProfileSettings,
  } = useSelector<RootState, AuthSliceState>((state) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const status = useSelector(selectAuthStatus);

  const logout = useCallback(() => {
    // clear redux store
    dispatch(auth.slice.actions.logout());
    // reset api cache
    dispatch(siteApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    dispatch(publicApi.util.resetApiState());
    // reset preference store
    dispatch(preferenceSlice.actions.reset());
    // reset skinner box store
    dispatch(skinnerBoxSlice.actions.reset());
    // redirect to home page (open mode)
    navigate(Paths.PRIVATE.HOME);
  }, [navigate, dispatch]);

  const fundingProfileList = useMemo(() => {
    const bankMap = bankInfos;
    const eWalletMap = ewalletInfos;

    const paymentMap = {
      ...Object.fromEntries(
        Object.entries(bankMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.Bank }]),
      ),
      ...Object.fromEntries(
        Object.entries(eWalletMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.EWallet }]),
      ),
    };
    return (
      self?.member?.fundingProfiles?.map((item) => {
        return {
          ...item,
          icon: paymentMap[item.key]?.icon,
          name: paymentMap[item.key]?.name,
          accountName: self?.member?.fullname ?? "",
        };
      }) || []
    );
  }, [self?.member?.fundingProfiles, bankInfos, ewalletInfos, self?.member?.fullname]);

  const defaultBankAccount: BankAccount | null = useMemo(() => {
    const fundingProfile = fundingProfileList?.find((item) => item.isPrimary && item.type === FundingProfileType.Bank);
    if (fundingProfile) {
      return {
        bankAccNo: fundingProfile.accNo,
        bankAccName: fundingProfile.accountName,
        bankKey: fundingProfile.key,
        bankName: fundingProfile.name,
        bankIcon: fundingProfile.icon || null,
      };
    }
    return null;
  }, [fundingProfileList]);

  const isBlacklisted = useMemo(() => {
    return self?.member?.group?.group?.criteria?.type === GroupCriteriaType.Blacklist;
  }, [self]);

  // Temp virtual field (kz-api #3854) today; google-type credential once BE drops it.
  const isGoogleLogin = hasGoogleCredential === true || isGoogleCredential(credential);

  const userMode = useMemo(() => {
    return status === AuthStatus.Authenticated ? UserMode.MEMBER : UserMode.GUEST;
  }, [status]);

  // Sync blacklist status from WS
  useWSBlacklistUpdate({
    cb: async (event: WSBlacklistUpdateEvent) => {
      const result = await dispatch(
        authApi.endpoints.getProfile.initiate(undefined, {
          forceRefetch: true,
        }),
      );
      if (result.data) {
        dispatch(authSlice.actions.updateProfile(result.data));
      }
      // Call onBlacklistUpdate callback if provided
      options?.onBlacklistUpdate?.(event.detail);
    },
  });

  const fundingProfileConfigs = useMemo(() => {
    const bankLimit = fundingProfileSettings?.supportedTypes?.bank?.limit ?? 0;
    const ewalletLimit = fundingProfileSettings?.supportedTypes?.["e-wallet"]?.limit ?? 0;
    const bankSort = fundingProfileSettings?.supportedTypes?.bank?.sort ?? 0;
    const ewalletSort = fundingProfileSettings?.supportedTypes?.["e-wallet"]?.sort ?? 0;

    return {
      bankLimit,
      ewalletLimit,
      bankSort,
      ewalletSort,
      isSingleBank: bankLimit === 1,
      isMultipleBank: bankLimit > 1,
      isSingleEWallet: ewalletLimit === 1,
      isMultipleEWallet: ewalletLimit > 1,
    };
  }, [fundingProfileSettings]);

  return {
    self,
    oauth,
    status,
    /**
     * @deprecated use useFundingProfile instead
     */
    defaultBankAccount,
    /**
     * @deprecated use useFundingProfile instead
     */
    fundingProfileList,
    logout,
    bankInfos,
    ewalletInfos,
    userMode,
    latestDepositAmount,
    isBlacklisted,
    credential,
    isGoogleLogin,
    googleAccessKey,

    fundingProfileSettings,
    fundingProfileConfigs,
    legalName: self?.member?.fullname?.trim(),
    isAuthenticated: status === AuthStatus.Authenticated,
  };
};

export default useAuthSession;
